import React, { forwardRef } from 'react';
import { StyleSheet, View } from 'react-native';

import { BodyText, Container, DisplayText, theme, useResponsive } from '@sg/garnish';
import type { AllergenTypes } from '@sg/shared-fe-features-menu';

import { useHomeContent } from '../../hooks';
import { HomeCategory } from '../HomeCategory';
import { HomeProductCard } from '../HomeProductCard';

// ─────────────────────────────────────────────────────────────────────────────

export const HomeGenericCategories = forwardRef<View, HomeGenericCategoriesProps>((props, ref) => {
  const {
    genericCategories,
    dietaryPreferences,
    openMenu,
    openProductDetails,
    addProductToBag,
    trackItemSelection,
  } = props;

  const { currentBreakpoint, match } = useResponsive();
  const dividerStyle = match([styles.categoryWithDividerXS, styles.categoryWithDividerSM]);
  const lastIndex = genericCategories.length - 1;

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <Container ref={ref} style={match([styles.containerXS, styles.containerSM])}>
      {genericCategories.map((category, index) => (
        <View key={category.id} style={[styles.category, index < lastIndex ? dividerStyle : null]}>
          {currentBreakpoint.isXS ? (
            <BodyText style={styles.titleXS} sizeMatch={['18']}>
              {category.categoryName}
            </BodyText>
          ) : (
            <DisplayText style={styles.titleSM} sizeMatch={['32']}>
              {category.categoryName}
            </DisplayText>
          )}

          <HomeCategory>
            {category.products.slice(0, 3).map((product) => (
              <HomeProductCard
                withMaxWidth
                key={product.lineageKey}
                isEnriched={product.isEnriched}
                categoryName={category.categoryName}
                id={product.id}
                slug={product.slug}
                title={product.title}
                cost={product.cost}
                description={product.description}
                cloudinaryImage={product.cloudinaryImage}
                contentfulImage={product.contentfulImage}
                isModifiable={product.isModifiable}
                dietaryPreferences={dietaryPreferences}
                allergenProperties={product.allergenProperties}
                calories={category.showCalories ? product.calories : 0}
                calorieOverrideText={product.calorieOverrideText}
                totalFatG={category.showFat ? product.totalFatG : 0}
                totalCarbsG={category.showCarbs ? product.totalCarbsG : 0}
                dietaryFiberG={category.showFiber ? product.dietaryFiberG : 0}
                sugarsG={category.showSugars ? product.sugarsG : 0}
                proteinG={category.showProtein ? product.proteinG : 0}
                co2eKg={category.showCo2 ? product.co2eKg : 0}
                openMenu={openMenu}
                openProductDetails={openProductDetails}
                addProductToBag={addProductToBag}
                trackItemSelection={trackItemSelection}
              />
            ))}
          </HomeCategory>
        </View>
      ))}
    </Container>
  );
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerXS: {
    paddingHorizontal: 0,
    gap: theme.spacing['4'],
  },
  containerSM: {
    alignItems: 'center',
    gap: theme.spacing['8'],
  },
  titleXS: {
    paddingHorizontal: theme.spacing['6'],
  },
  titleSM: {
    textAlign: 'center',
  },
  category: {
    gap: theme.spacing['4'],
  },
  categoryWithDividerXS: {
    gap: theme.spacing['4'],
    paddingBottom: theme.spacing['4'],
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
  },
  categoryWithDividerSM: {
    gap: theme.spacing['4'],
    paddingBottom: theme.spacing['8'],
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type HomeGenericCategoriesProps = {
  genericCategories: ReturnType<typeof useHomeContent>['genericCategories'];
  dietaryPreferences: AllergenTypes;
  openMenu: () => void;
  openProductDetails: (slug: string) => void;
  addProductToBag: (id: string, title: string) => Promise<void>;
  trackItemSelection: (product: string, category: string) => void;
};
